import {
  Table, Header, Box, SpaceBetween, Button, Modal, FormField, Input, Select,
  StatusIndicator, Flashbar, FlashbarProps,
} from "@cloudscape-design/components";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "./auth";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";

const ASSET_TYPES = [
  { value: "ec2-instance", label: "EC2 Instance" },
  { value: "lambda-function", label: "Lambda Function" },
  { value: "api-gateway", label: "API Gateway" },
  { value: "ecs-service", label: "ECS Service" },
  { value: "rds-instance", label: "RDS Instance" },
  { value: "dynamodb-table", label: "DynamoDB Table" },
  { value: "fm-system-defined-inference-profile", label: "FM SDIP" },
] as const;

// Types that cannot be synced through the registry-based /inventory/sync flow.
const NON_SYNCABLE_TYPES = new Set<string>(["fm-system-defined-inference-profile"]);

// Asset types without a `stack` relation (not deployed via a CFN stack).
const NO_STACK_TYPES = new Set<string>(["fm-system-defined-inference-profile"]);

const PLURAL_MAP: Record<string, string> = {
  "ec2-instance": "ec2-instances",
  "lambda-function": "lambda-functions",
  "api-gateway": "api-gateways",
  "ecs-service": "ecs-services",
  "rds-instance": "rds-instances",
  "dynamodb-table": "dynamodb-tables",
  "fm-system-defined-inference-profile": "fm-system-defined-inference-profiles",
};

interface Asset {
  documentId: string;
  assetType: string;
  arn: string;
  Region: string;
  status: string;
  account: { Alias: string; accountid: string } | null;
  stack: { documentId: string; name: string } | null;
  lastSynced: string | null;
}

function statusType(s: string): "success" | "error" | "warning" | "info" {
  if (/active|running|available/i.test(s)) return "success";
  if (/stopped|failed|error/i.test(s)) return "error";
  if (/pending|creating|updating/i.test(s)) return "warning";
  return "info";
}

export default function InventoryPage() {
  useDocumentTitle("Inventory");
  const navigate = useNavigate();
  const apiKey = getAuthToken();

  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);

  // Filters
  const [filterType, setFilterType] = useState<string | null>(null);
  const [filterAccount, setFilterAccount] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Sync modal
  const [syncVisible, setSyncVisible] = useState(false);
  const [syncArn, setSyncArn] = useState("");
  const [syncType, setSyncType] = useState<string>(ASSET_TYPES[0].value);
  const [syncAccount, setSyncAccount] = useState("");
  const [syncRegion, setSyncRegion] = useState(getDefaultRegion());
  const [syncing, setSyncing] = useState(false);

  const fetchAll = useCallback(() => {
    if (!apiKey) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    const accountPopulate = "populate[account][fields][0]=Alias&populate[account][fields][1]=accountid";
    const stackPopulate = "populate[stack][fields][0]=name&populate[stack][fields][1]=documentId";
    // Strapi paginates at 25 items/page by default (maxLimit 100). Fetch every page.
    const fetchAllPages = async (plural: string, query: string): Promise<any[]> => {
      const items: any[] = [];
      let page = 1;
      while (true) {
        const r = await apiFetch(`/${plural}?${query}&pagination[page]=${page}&pagination[pageSize]=100`);
        if (!r.ok) throw new Error(`${plural}: ${r.status}`);
        const json = await r.json();
        items.push(...(json.data ?? []));
        const pageCount = json.meta?.pagination?.pageCount;
        if (!pageCount || page >= pageCount) break;
        page += 1;
      }
      return items;
    };
    Promise.all(
      Object.entries(PLURAL_MAP).map(([type, plural]) => {
        const query = NO_STACK_TYPES.has(type)
          ? accountPopulate
          : `${accountPopulate}&${stackPopulate}`;
        return fetchAllPages(plural, query)
          .then(data => data.map((item: any) => ({
            documentId: item.documentId,
            assetType: type,
            arn: item.arn || item.ARN || "—",
            Region: item.Region || item.region || "—",
            status: item.status || item.Status || "—",
            account: item.account || null,
            stack: item.stack || null,
            lastSynced: item.lastSynced || item.lastSyncedAt || item.updatedAt || null,
          })));
      })
    )
      .then(results => setAssets(results.flat()))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [apiKey]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = assets.filter(a =>
    (!filterType || a.assetType === filterType) &&
    (!filterAccount || (a.account?.Alias ?? "").toLowerCase().includes(filterAccount.toLowerCase())) &&
    (!filterRegion || a.Region.toLowerCase().includes(filterRegion.toLowerCase())) &&
    (!filterStatus || a.status.toLowerCase().includes(filterStatus.toLowerCase()))
  );

  const handleSync = () => {
    if (!apiKey || !syncArn.trim()) return;
    setSyncing(true);
    const headers: Record<string, string> = {};
    if (syncAccount) {
      const creds = getAwsCreds(syncAccount);
      if (creds) {
        headers["X-AWS-Access-Key-Id"] = creds.accessKeyId;
        headers["X-AWS-Secret-Access-Key"] = creds.secretAccessKey;
        headers["X-AWS-Session-Token"] = creds.sessionToken;
      }
    }
    apiFetch(`/inventory/sync`, {
      method: "POST",
      headers,
      body: JSON.stringify({ arn: syncArn, assetType: syncType, account: syncAccount, region: syncRegion }),
    })
      .then(r => { if (!r.ok) throw new Error(`Sync failed: ${r.status}`); return r.json(); })
      .then(() => {
        setFlash([{ type: "success", content: "Asset synced successfully", dismissible: true, onDismiss: () => setFlash([]) }]);
        setSyncVisible(false);
        setSyncArn("");
        fetchAll();
      })
      .catch(e => setFlash([{ type: "error", content: e.message, dismissible: true, onDismiss: () => setFlash([]) }]))
      .finally(() => setSyncing(false));
  };

  return (
    <SpaceBetween size="m">
      <Flashbar items={flash} />
      <Table
        variant="full-page"
        loading={loading}
        items={filtered}
        onRowClick={({ detail }) => navigate(`/inventory/${detail.item.assetType}/${detail.item.documentId}`)}
        filter={
          <SpaceBetween direction="horizontal" size="xs">
            <Select
              selectedOption={filterType ? { value: filterType, label: ASSET_TYPES.find(t => t.value === filterType)?.label } : null}
              onChange={({ detail }) => setFilterType(detail.selectedOption.value ?? null)}
              options={ASSET_TYPES.map(t => ({ value: t.value, label: t.label }))}
              placeholder="Asset type"
              selectedAriaLabel="Selected"
            />
            <Input value={filterAccount} onChange={({ detail }) => setFilterAccount(detail.value)} placeholder="Account" />
            <Input value={filterRegion} onChange={({ detail }) => setFilterRegion(detail.value)} placeholder="Region" />
            <Input value={filterStatus} onChange={({ detail }) => setFilterStatus(detail.value)} placeholder="Status" />
            {(filterType || filterAccount || filterRegion || filterStatus) && (
              <Button onClick={() => { setFilterType(null); setFilterAccount(""); setFilterRegion(""); setFilterStatus(""); }}>Clear</Button>
            )}
          </SpaceBetween>
        }
        columnDefinitions={[
          { id: "arn", header: "ARN", cell: a => a.arn },
          { id: "assetType", header: "Asset Type", cell: a => ASSET_TYPES.find(t => t.value === a.assetType)?.label ?? a.assetType },
          { id: "region", header: "Region", cell: a => a.Region },
          { id: "status", header: "Status", cell: a => <StatusIndicator type={statusType(a.status)}>{a.status}</StatusIndicator> },
          { id: "account", header: "Account", cell: a => a.account ? `${a.account.Alias} (${a.account.accountid})` : "—" },
          { id: "stack", header: "Stack", cell: a => a.stack ? a.stack.name : "—" },
          { id: "lastSynced", header: "Last Synced", cell: a => a.lastSynced ? new Date(a.lastSynced).toLocaleString() : "—" },
        ]}
        header={
          <Header
            counter={`(${filtered.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" loading={loading} onClick={fetchAll} />
                <Button variant="primary" onClick={() => setSyncVisible(true)}>Sync Asset</Button>
              </SpaceBetween>
            }
          >
            Inventory
          </Header>
        }
        empty={
          <Box textAlign="center" color={error ? "text-status-error" : "text-body-secondary"}>
            {error ? `Error: ${error}` : !apiKey ? "No API key configured. Go to Settings to add one." : "No inventory assets found"}
          </Box>
        }
      />
      <Modal
        visible={syncVisible}
        onDismiss={() => setSyncVisible(false)}
        header="Sync Asset"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setSyncVisible(false)}>Cancel</Button>
              <Button variant="primary" loading={syncing} onClick={handleSync}>Sync</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="ARN">
            <Input value={syncArn} onChange={({ detail }) => setSyncArn(detail.value)} placeholder="arn:aws:..." />
          </FormField>
          <FormField label="Asset Type">
            <Select
              selectedOption={ASSET_TYPES.find(t => t.value === syncType) ?? null}
              onChange={({ detail }) => setSyncType(detail.selectedOption.value!)}
              options={ASSET_TYPES.filter(t => !NON_SYNCABLE_TYPES.has(t.value)).map(t => ({ value: t.value, label: t.label }))}
            />
          </FormField>
          <FormField label="Account (alias)">
            <Input value={syncAccount} onChange={({ detail }) => setSyncAccount(detail.value)} placeholder="e.g. my-account" />
          </FormField>
          <FormField label="Region">
            <Input value={syncRegion} onChange={({ detail }) => setSyncRegion(detail.value)} placeholder="e.g. eu-west-1" />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
